import {
    BundleNewDeviceSchema,
    BundleNewDicts,
    BundleNewMode,
    BundleNewModeGroup,
    DeviceBundlePayload,
    DeviceBundlePreflight,
} from '../../../api/bundle';
import { MMGroup, MMMode } from '../../../api/mmode';
import {
    FieldGroupDraftBody,
    FieldGroupEditorState,
} from '../../../components/schema/FieldGroupEditorModal';
import {
    draftGroupItem,
    editorFromDraftGroup,
    parseGroupFields,
    upsertBy,
} from '../../../utils/bundleNewDicts';

// 随套件新建的字典 (payload.newDicts, 2026-09-29).
//
// 向导里新建的模式组 / 模式值 / 字段组不再单独提审, 而是记进套件草稿, 随整包一个工单落库:
// 单独提审时工单没过, 字典里没有这些值, 向导勾不上、预检也拒绝引用, 只能等审批结束再回来。
// 这里是几个步骤共用的换算: 草稿项 → 与字典接口同形的行 (UI 照常渲染), 以及增删改。
// 草稿项的 id 恒为 0 —— 列表里用 isPendingMode / isPendingGroup 判断"待随套件新建"。

/** 草稿模式组 → MMGroup 形态 (id=0). */
export function pendingGroupsOf(nd?: BundleNewDicts): MMGroup[] {
    return (nd?.modeGroups || []).map((g) => ({
        id: 0,
        uuid: '',
        code: g.code,
        nameZh: g.nameZh || '',
        nameEn: g.nameEn || '',
        archCodes: g.archCodes || [],
        sort: 0,
        isEnabled: g.isEnabled !== false,
    }));
}

/** 草稿模式值 → MMMode 形态 (id=0). */
export function pendingModesOf(nd?: BundleNewDicts): MMMode[] {
    return (nd?.modes || []).map((m) => ({
        id: 0,
        uuid: '',
        value: m.value,
        nameZh: m.nameZh || '',
        nameEn: m.nameEn || '',
        groupCode: m.groupCode,
        sort: 0,
        isEnabled: m.isEnabled !== false,
    }));
}

/** 字典清单并上草稿项 (同键以字典为准: 字典里已有就不该再新建, 预检也会拦).
 * 草稿项排在最前且最新的在最上: 刚新建的要一眼看到, 排在几十个字典项后面还得往下翻找. */
export function withPendingModes(
    list: MMMode[],
    nd?: BundleNewDicts,
): MMMode[] {
    const have = new Set(list.map((m) => m.value));
    const pending = pendingModesOf(nd).filter((m) => !have.has(m.value));
    return [...pending.reverse(), ...list];
}

export function withPendingGroups(
    list: MMGroup[],
    nd?: BundleNewDicts,
): MMGroup[] {
    const have = new Set(list.map((g) => g.code));
    const pending = pendingGroupsOf(nd).filter((g) => !have.has(g.code));
    return [...pending.reverse(), ...list];
}

export function isPendingMode(nd: BundleNewDicts | undefined, value: string) {
    return !!nd?.modes?.some((m) => m.value === value);
}

export function isPendingGroup(nd: BundleNewDicts | undefined, code: string) {
    return !!nd?.modeGroups?.some((g) => g.code === code);
}

export function isPendingSchema(
    nd: BundleNewDicts | undefined,
    groupKey: string,
) {
    return !!nd?.deviceSchemas?.some((s) => s.groupKey === groupKey);
}

/** 空段去掉, 全空时整个 newDicts 去掉 (草稿与提交载荷都保持干净). */
function compact(nd: BundleNewDicts): BundleNewDicts | undefined {
    const out: BundleNewDicts = {};
    if (nd.modeGroups?.length) out.modeGroups = nd.modeGroups;
    if (nd.modes?.length) out.modes = nd.modes;
    if (nd.deviceSchemas?.length) out.deviceSchemas = nd.deviceSchemas;
    return Object.keys(out).length ? out : undefined;
}

export function upsertNewGroup(
    nd: BundleNewDicts | undefined,
    g: Partial<MMGroup> & { code: string },
): BundleNewDicts | undefined {
    const item: BundleNewModeGroup = {
        code: g.code,
        nameZh: g.nameZh || undefined,
        nameEn: g.nameEn || undefined,
        archCodes: g.archCodes?.length ? g.archCodes : undefined,
        isEnabled: g.isEnabled !== false,
    };
    return compact({
        ...nd,
        modeGroups: upsertBy(nd?.modeGroups, item, (x) => x.code),
    });
}

export function upsertNewMode(
    nd: BundleNewDicts | undefined,
    m: Partial<MMMode> & { value: string },
): BundleNewDicts | undefined {
    const item: BundleNewMode = {
        value: m.value,
        groupCode: m.groupCode || '',
        nameZh: m.nameZh || undefined,
        nameEn: m.nameEn || undefined,
        isEnabled: m.isEnabled !== false,
    };
    return compact({
        ...nd,
        modes: upsertBy(nd?.modes, item, (x) => x.value),
    });
}

export function upsertNewSchema(
    nd: BundleNewDicts | undefined,
    body: FieldGroupDraftBody,
): BundleNewDicts | undefined {
    const item: BundleNewDeviceSchema = {
        ...draftGroupItem(body),
        ...(body.deviceKeys?.length ? { deviceKeys: body.deviceKeys } : {}),
    };
    return compact({
        ...nd,
        deviceSchemas: upsertBy(nd?.deviceSchemas, item, (x) => x.groupKey),
    });
}

/** 撤销一个草稿模式值: 连同第 ② 步对它的勾选一起去掉 (勾着一个不会被建的值, 预检必拒). */
export function removeNewMode(
    p: DeviceBundlePayload,
    value: string,
): DeviceBundlePayload {
    return {
        ...p,
        newDicts: compact({
            ...p.newDicts,
            modes: (p.newDicts?.modes || []).filter((m) => m.value !== value),
        }),
        modeRefs: (p.modeRefs || []).filter((r) => r.modeValue !== value),
    };
}

export function removeNewGroup(
    p: DeviceBundlePayload,
    code: string,
): DeviceBundlePayload {
    return {
        ...p,
        newDicts: compact({
            ...p.newDicts,
            modeGroups: (p.newDicts?.modeGroups || []).filter(
                (g) => g.code !== code,
            ),
        }),
    };
}

/** 撤销一个草稿字段组: 连同已填的值与绑定一起去掉. */
export function removeNewSchema(
    p: DeviceBundlePayload,
    groupKey: string,
): DeviceBundlePayload {
    const ext = { ...(p.extValues || {}) };
    delete ext[groupKey];
    const bound = (p.schemaBindings?.deviceSchemas || []).filter(
        (k) => k !== groupKey,
    );
    return {
        ...p,
        newDicts: compact({
            ...p.newDicts,
            deviceSchemas: (p.newDicts?.deviceSchemas || []).filter(
                (s) => s.groupKey !== groupKey,
            ),
        }),
        extValues: ext,
        schemaBindings: { ...(p.schemaBindings || {}), deviceSchemas: bound },
    };
}

type PreflightGroup = DeviceBundlePreflight['deviceSchemas'][number];

/** 草稿字段组 → preflight 同形 (第 ③ 步照常渲染表单; 停用的组不列, 同 preflight 口径). */
export function pendingSchemaGroupsOf(nd?: BundleNewDicts): PreflightGroup[] {
    return (nd?.deviceSchemas || [])
        .filter((s) => s.isEnabled !== false)
        .map(
            (s) =>
                ({
                    groupKey: s.groupKey,
                    label: s.label,
                    layout: s.layout || 'flat',
                    rowSource: s.rowSource || '',
                    rowLabel: s.rowLabel || '',
                    deviceKeys: s.deviceKeys?.length ? s.deviceKeys : null,
                    fieldLayout: s.fieldLayout || null,
                    fields: parseGroupFields(s.fields) as unknown as Record<
                        string,
                        unknown
                    >[],
                }) as PreflightGroup,
        );
}

/** 草稿字段组回填编辑器 (归属锁定当前框架). */
export function editorFromPendingSchema(
    s: BundleNewDeviceSchema,
    archCode: string,
): FieldGroupEditorState {
    return editorFromDraftGroup(s, { archCode });
}
